import { createContext, useState, useEffect, ReactNode, useMemo, useRef } from 'react'
import { billing } from '@services/billing/billing-service'
import { Link, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@heroui/react'
import { SubscriptionData, SubscriptionStatus } from '@interfaces/auth'
import Pricing from '@/components/billing/pricing'
import LoadingIndicator from '@/components/ui/loading-indicator'
import { useTemporaryStore } from './temporaryStore'
import { useLocation } from 'react-router-dom'

interface BillingContext {
  subscription: SubscriptionData | undefined
  active: boolean
  trialingAndCappedDocs: boolean
  onSubscribe: (errorCode?: BillingModalCopyKey) => void
  updateSubscriptionMetadata: (metadata: Record<string, any>) => void
}

export const billingContext = createContext<BillingContext>({
  subscription: undefined,
  active: false,
  trialingAndCappedDocs: true,
  onSubscribe: () => console.log('SUBSCRIBE'),
  updateSubscriptionMetadata: () => console.log('UPDATE_SUBSCRIPTION_METADATA'),
})

const replacePlaceholders = (
  text: string,
  productMetadata?: {
    trialExportCap?: number
    trialDocsCap?: number
    trialAiImprovementCap?: number
    trialReviewsCap?: number
  } | null
) => {
  if (!text) return undefined

  let result = text
  result = result.replace(/%trialExportCap%/g, String(productMetadata?.trialExportCap ?? 2))
  result = result.replace(/%trialDocsCap%/g, String(productMetadata?.trialDocsCap ?? 3))
  result = result.replace(/%trialAiImprovementCap%/g, String(productMetadata?.trialAiImprovementCap ?? 6))
  result = result.replace(/%trialReviewsCap%/g, String(productMetadata?.trialReviewsCap ?? 5))
  return result
}

const getBillingModalCopy = (
  productMetadata?: {
    trialExportCap?: number
    trialDocsCap?: number
    trialAiImprovementCap?: number
    trialReviewsCap?: number
    copy?: any
  } | null
) => {
  return {
    TRIAL_LIMIT_EXPORT_COUNT: {
      title:
        replacePlaceholders(productMetadata?.copy?.TRIAL_LIMIT_EXPORT_COUNT?.title, productMetadata) ||
        'Ready to send your document?',
      body:
        replacePlaceholders(productMetadata?.copy?.TRIAL_LIMIT_EXPORT_COUNT?.body, productMetadata) ||
        `You've used your ${productMetadata?.trialExportCap ?? 2} free exports. Upgrade to download, share, and send unlimited documents instantly.`,
      ctaText: productMetadata?.copy?.TRIAL_LIMIT_EXPORT_COUNT?.ctaText || 'Upgrade to continue →',
    },
    TRIAL_LIMIT_DOCS_COUNT: {
      title:
        replacePlaceholders(productMetadata?.copy?.TRIAL_LIMIT_DOCS_COUNT?.title, productMetadata) ||
        'Create more documents with Cobrief',
      body:
        replacePlaceholders(productMetadata?.copy?.TRIAL_LIMIT_DOCS_COUNT?.body, productMetadata) ||
        `You've reached your ${productMetadata?.trialDocsCap ?? 3} free documents. Upgrade to continue creating without limits.`,
      ctaText: productMetadata?.copy?.TRIAL_LIMIT_DOCS_COUNT?.ctaText || 'Upgrade to continue →',
    },
    TRIAL_LIMIT_AI_IMPROVEMENTS: {
      title:
        replacePlaceholders(productMetadata?.copy?.TRIAL_LIMIT_AI_IMPROVEMENTS?.title, productMetadata) ||
        'Finish improving your document',
      body:
        replacePlaceholders(productMetadata?.copy?.TRIAL_LIMIT_AI_IMPROVEMENTS?.body, productMetadata) ||
        'Upgrade to Cobrief AI for unlimited one-click improvements and editing tools.',
      ctaText: productMetadata?.copy?.TRIAL_LIMIT_AI_IMPROVEMENTS?.ctaText || 'Upgrade to continue →',
    },
    TRIAL_LIMIT_REVIEWS_COUNT: {
      title:
        replacePlaceholders(productMetadata?.copy?.TRIAL_LIMIT_REVIEWS_COUNT?.title, productMetadata) ||
        'Continue improving your documents',
      body:
        replacePlaceholders(productMetadata?.copy?.TRIAL_LIMIT_REVIEWS_COUNT?.body, productMetadata) ||
        `You've used your ${productMetadata?.trialReviewsCap ?? 5} free reviews. Upgrade to continue getting unlimited AI-powered reviews.`,
      ctaText: productMetadata?.copy?.TRIAL_LIMIT_REVIEWS_COUNT?.ctaText || 'Upgrade to continue →',
    },
    PAID_LIMIT_EXPORT_COUNT: {
      title: productMetadata?.copy?.PAID_LIMIT_EXPORT_COUNT?.title || 'Daily Export Limit Reached',
      body:
        productMetadata?.copy?.PAID_LIMIT_EXPORT_COUNT?.body ||
        'You have reached your daily export limit. To ensure fair usage and system stability, we have a daily cap. Please come back tomorrow to continue exporting documents/templates.',
      ctaText: productMetadata?.copy?.PAID_LIMIT_EXPORT_COUNT?.ctaText || '',
    },
    NO_SUBSCRIPTION: {
      title:
        replacePlaceholders(productMetadata?.copy?.NO_SUBSCRIPTION?.title, productMetadata) ||
        'Continue with full Cobrief access',
      body:
        replacePlaceholders(productMetadata?.copy?.NO_SUBSCRIPTION?.body, productMetadata) ||
        'Upgrade to keep working without limits — editing, exports, and AI.',
      ctaText: productMetadata?.copy?.NO_SUBSCRIPTION?.ctaText || 'Upgrade now',
    },
  }
}

export type BillingModalCopyKey = keyof ReturnType<typeof getBillingModalCopy>

interface BillingProviderProps {
  children: ReactNode
}

/**
 * BillingProvider manages subscription state and displays the pricing modal when user doesn't have an active subscription
 * The modal contains embedded Stripe checkout that completes payment without leaving the app
 */
export const BillingProvider = ({ children }: BillingProviderProps) => {
  const [loading, setLoading] = useState(true)
  const [showSubModal, setShowSubModal] = useState(false)
  const [subscription, setSubscription] = useState<SubscriptionData | undefined>()
  const [errorCode, setErrorCode] = useState<BillingModalCopyKey | undefined>()
  const location = useLocation()
  const lastPathnameRef = useRef(location.pathname)
  const { productMetadata, setProductMetadata, setProduct } = useTemporaryStore(s => ({
    productMetadata: s.productMetadata,
    setProductMetadata: s.setProductMetadata,
    setProduct: s.setProduct,
  }))

  const verifyBilling = async ({ showLoading = true }: { showLoading?: boolean } = {}) => {
    if (showLoading) setLoading(true)

    try {
      const [billingResponse, productResponse] = await Promise.all([
        billing.getBilling(),
        billing.getActiveProductMetadata(),
      ])

      setSubscription(billingResponse.data.subscription)

      if (productResponse?.data) {
        setProductMetadata(productResponse.data.product.metadata)
      }
    } catch {
      // handle error
    } finally {
      setLoading(false)
    }
  }

  const updateSubscriptionMetadata = (metadata: Record<string, any>) => {
    setSubscription(prev => {
      if (!prev) return prev
      return {
        ...prev,
        metadata: {
          ...prev.metadata,
          ...metadata,
        },
      }
    })
  }

  const cacheProducts = async () => {
    // billing modal will not open for active users
    // it also has on-demand fallback if not cached in Pricing modal component
    if (subscription?.status === SubscriptionStatus.Active) return

    billing
      .getProducts()
      .then(({ data }) => {
        setProduct(data.products?.[0])
      })
      .catch(() => {})
  }

  const active = useMemo(() => {
    switch (subscription?.status) {
      case SubscriptionStatus.Trialing:
      case SubscriptionStatus.Active:
        return true
      default:
        return false
    }
  }, [subscription?.status])

  const trialingAndCappedDocs = useMemo(() => {
    if (!subscription?.status) return true

    // active: unlimited reviews
    if (subscription.status === SubscriptionStatus.Active) return false

    // trialing: unlimited reviews up to the configured limit (default: 3)
    const docsCount = subscription.metadata?.docsCount || 0
    const trialDocsCap = productMetadata?.trialDocsCap ?? 3
    if (subscription.status === SubscriptionStatus.Trialing && docsCount < trialDocsCap) return false

    return true
  }, [subscription?.status, subscription?.metadata?.docsCount, productMetadata?.trialDocsCap])

  const modalCopy = useMemo(() => {
    const BILLING_MODAL_COPY = getBillingModalCopy(productMetadata)
    return BILLING_MODAL_COPY[errorCode || 'NO_SUBSCRIPTION'] || BILLING_MODAL_COPY.NO_SUBSCRIPTION
  }, [errorCode, productMetadata])

  // useEffect(() => {
  //   if (!active && !loading) {
  //     setShowSubModal(true)
  //   } else if (active) {
  //     // Close modal if subscription becomes active
  //     setShowSubModal(false)
  //   }
  // }, [active, loading])

  useEffect(() => {
    cacheProducts()
    verifyBilling({ showLoading: true })
  }, [])

  // current doc metadata must be up to date if not active
  useEffect(() => {
    if (lastPathnameRef.current === null) {
      lastPathnameRef.current = location.pathname
      return
    }

    if (subscription?.status === SubscriptionStatus.Active) return

    if (lastPathnameRef.current !== location.pathname) {
      lastPathnameRef.current = location.pathname
      verifyBilling({ showLoading: false })
    }
  }, [location.pathname, subscription?.status])

  if (loading) {
    return <LoadingIndicator />
  }
  return (
    <billingContext.Provider
      value={{
        active,
        subscription,
        trialingAndCappedDocs,
        onSubscribe: (code?: BillingModalCopyKey) => {
          setErrorCode(code)
          setShowSubModal(true)
        },
        updateSubscriptionMetadata,
      }}
    >
      {children}
      <Modal backdrop="blur" size={'xl'} isOpen={showSubModal} onClose={() => setShowSubModal(false)}>
        <ModalContent>
          {() => (
            <>
              <ModalHeader className="flex-col pt-8 text-center">
                <h2 className="mb-2 text-2xl font-bold">{modalCopy.title}</h2>
                <p className="mt-2 text-medium text-gray-600">{modalCopy.body}</p>
              </ModalHeader>
              {modalCopy.ctaText !== '' && (
                <>
                  <ModalBody>
                    <Pricing
                      onSuccess={() => {
                        setShowSubModal(false)
                        verifyBilling()
                      }}
                      ctaText={modalCopy.ctaText}
                    />
                  </ModalBody>
                  <ModalFooter className="my-4 mt-0 block py-0 text-center text-xs text-default-400">
                    By continuing, you agree to Cobrief's{' '}
                    <Link
                      href="/static/terms-and-conditions.pdf"
                      download="Cobrief Terms and Conditions (2025).pdf"
                      className="text-xs"
                    >
                      Terms and Conditions
                    </Link>
                  </ModalFooter>
                </>
              )}
            </>
          )}
        </ModalContent>
      </Modal>
    </billingContext.Provider>
  )
}
